import React, { ReactNode } from "react"

import { ArrowLeftIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import { Form, Link, useActionData, useLoaderData } from "react-router-dom"
import { twMerge } from "tailwind-merge"

import { Bee } from "~/src/components/BeeKit"
import { InventoryItemDetails, InventoryItemDetailsProps } from "~/src/components/Dashboard/InventoryItemDetails"
import { MainHeading } from "~/src/components/MainHeading"
import { iname, PathBuilder } from "~/src/lib/iname"

export type CollectionItem = InventoryItemDetailsProps & {
  collectionableType: string
  collectionableId: string
  nameOverride: string
}

export type DashboardStoreCollectionItemSettingsProps = {
  title: string
  backHref: string
  name: string
  reorderHref?: string
  storeUrl: string
  item: CollectionItem
}

export type DashboardStoreCollectionItemSettingsErrors = {
  errors?: {
    collectionable?: { nameOverride?: string[] }
  }
}

const itemName = iname().collectionItem

export function DashboardStoreCollectionItemSettings() {
  const { title, backHref, item, reorderHref, storeUrl } = useLoaderData<DashboardStoreCollectionItemSettingsProps>()
  const { errors } = useActionData<DashboardStoreCollectionItemSettingsErrors>() ?? {}

  return (
    <div className="my-3 flex flex-col bg-gray-50 @container">
      <Link
        to={backHref}
        className="mx-6 mr-auto flex cursor-pointer items-center gap-2 text-lg text-gray-500 hover:underline"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Back
      </Link>

      <hgroup className="mx-6 flex flex-col items-center @6xl:mx-6 @6xl:flex-row @6xl:gap-4">
        <MainHeading title={title} className="ml-0 mr-auto" />
        <div className="mb-4 flex w-full flex-col justify-between gap-2 @2xl:flex-row @2xl:justify-end @6xl:m-0 @6xl:w-fit">
          {reorderHref ? (
            <Bee.ButtonAnchor href={reorderHref} kind="secondary" className="flex items-center gap-3">
              <ArrowPathIcon className="h-6 w-6" />
              <span>Reorder item</span>
            </Bee.ButtonAnchor>
          ) : null}

          <Bee.ButtonAnchor href={storeUrl} kind="secondary" className="flex items-center gap-2">
            <ArrowTopRightOnSquareIcon className="h-6 w-6" />
            <span>View product in store</span>
          </Bee.ButtonAnchor>
        </div>
      </hgroup>

      <Form method="patch" className="flex flex-col gap-6 @2xl:mx-6">
        <Bee.HiddenInput name={itemName.collectionableType} value={item.collectionableType} />
        <Bee.HiddenInput name={itemName.collectionableId} value={item.collectionableId} />
        <Bee.HiddenInput name={itemName.collectionableAttributes.id} value={item.collectionableId} />

        <div className="flex flex-col divide-y border border-gray-200 bg-white @2xl:rounded-lg">
          <section className="flex flex-col gap-6 p-6">
            <InventoryItemDetails {...item} />
          </section>

          <section className="flex flex-col gap-6 p-6">
            <hgroup className="flex flex-col">
              <h1 className="text-lg font-medium text-gray-900">Display Information</h1>
              <p className="text-sm text-gray-500">
                Editing the information in this section will update how the product appears on your store site
              </p>
            </hgroup>

            <div>
              <div className="flex flex-col">
                <SettingsField
                  label="Display name"
                  name={itemName.collectionableAttributes.nameOverride}
                  errors={errors?.collectionable?.nameOverride}
                >
                  <Bee.Input className="w-full @xl:w-auto @xl:grow @2xl:col-start-2" defaultValue={item.nameOverride} />
                </SettingsField>
              </div>
            </div>
          </section>

          <section className="flex flex-col gap-6 p-6">
            <h1 className="text-lg font-medium text-gray-900">Product Settings</h1>
          </section>
        </div>

        <div className="mx-6 flex @2xl:mx-0">
          <Bee.Button className="grow" type="submit">
            Save updates
          </Bee.Button>
        </div>
      </Form>
    </div>
  )
}

// #region Private
type SettingsFieldProps = {
  label: ReactNode
  name: string | PathBuilder
  errors?: string[]
  children?: ReactNode
  hint?: ReactNode
  className?: string
}

function SettingsField(props: SettingsFieldProps) {
  const { label, name, errors, children, hint, className } = props

  return (
    <Bee.FieldProvider label={label} name={name} errorMessages={errors}>
      <div className={twMerge("grid grid-cols-1 gap-x-4 gap-y-1 @2xl:grid-cols-[10rem,1fr]", className)}>
        <Bee.FieldLabel className="self-center" />
        {children}
        <Bee.FieldErrors className="hidden data-[errored]:block @2xl:col-start-2" />
        <Bee.FieldHint className="@2xl:col-start-2">{hint}</Bee.FieldHint>
      </div>
    </Bee.FieldProvider>
  )
}
// #endregion
